import { css } from '@emotion/core';
import { Button, Collapse, Dialog, List, ListItem, ListItemText } from '@material-ui/core';
import { ExpandLess, ExpandMore } from '@material-ui/icons';
import { Map } from 'immutable';
import { flatMap, groupBy, orderBy, partition, startCase } from 'lodash';
import * as React from 'react';
import { connect } from 'react-redux';

import { EmailTemplateView } from 'shared/models/email-templates';
import { Permissions } from 'shared/models/permission';
import { hasRole, UserData } from 'shared/models/user';

import { deleteTemplate, getConfirmation, getEmailTemplates, saveTemplate, showModalAlert } from '../actions';
import { Spinner } from '../core-ui/spinner';
import { RequestErrors, State } from '../state';
import { EmailTemplate } from './email-template';

const styles = css`
    .MuiDialog-paper {
        width: 60%;
    }
`;

interface ConnectedProps {
    emailTemplates: EmailTemplateView[];
    pendingRequests: Map<string, RequestErrors>;
    user: UserData;
    userPermissions: Permissions;
}

interface ConnectedDispatch {
    deleteTemplate: (template: Partial<EmailTemplateView>) => void;
    getConfirmation: (onConfirm: () => void, description?: string | JSX.Element, title?: string) => void;
    getEmailTemplates: (group: string) => void;
    saveTemplate: (data: Partial<EmailTemplateView>) => void;
    showModalAlert: (description: string | JSX.Element, title: string) => void;
}

type EmailTemplatesProps = ConnectedProps & ConnectedDispatch;

interface EmailTemplatesState {
    selected: Partial<EmailTemplateView>;
    openSections: { [key: string]: boolean };
}

class EmailTemplatesComponent extends React.Component<EmailTemplatesProps, EmailTemplatesState> {
    constructor(props: EmailTemplatesProps) {
        super(props);
        props.getEmailTemplates(undefined);
        this.state = { selected: null, openSections: {} };
    }

    handleSelectTemplate = (selected: EmailTemplateView) => () => {
        this.setState({ selected });
    };

    handleToggleSection = (sectionKey: string) => () => {
        this.setState((prevState) => ({
            openSections: {
                ...prevState.openSections,
                [sectionKey]: !prevState.openSections[sectionKey]
            }
        }));
    };

    handleCloseTemplate = () => {
        this.setState({ selected: null });
    };

    handleSaveTemplate = (data: Partial<EmailTemplateView>) => {
        this.props.saveTemplate(data);
        this.setState({ selected: null });
    };

    handleAddTemplate = () => {
        const newTemplate: Partial<EmailTemplateView> = {
            body: '',
            group: 'generic',
            id: null,
            kind: '',
            subject: '',
            userId: this.props.user.id
        };
        this.setState({ selected: newTemplate });
    };

    handleDelete = (template: Partial<EmailTemplateView>) => () => {
        this.props.getConfirmation(
            () => {
                this.props.deleteTemplate(template);
                this.setState({ selected: null });
            },
            'This email template will be deleted immediately.',
            'Confirm'
        );
    };

    render() {
        const { emailTemplates, pendingRequests, user, userPermissions } = this.props;
        const { selected, openSections } = this.state;
        if (emailTemplates.length === 0) return <Spinner />;

        const [generic, others] = partition(emailTemplates, (t) => t.group === 'generic');
        const [personal, shared] = partition(generic, (t) => t.userId === user.id);
        const personalTemplates = orderBy(personal, ['kind', 'id']).map((t) => (
            <ListItem button={true} key={t.id} onClick={this.handleSelectTemplate(t)} style={{ paddingLeft: 32 }}>
                <ListItemText primary={startCase(t.kind)} />
            </ListItem>
        ));
        const sharedTemplates = orderBy(
            shared.filter((t) => !t.userId),
            ['kind', 'id']
        ).map((t) => (
            <ListItem button={true} key={t.id} onClick={this.handleSelectTemplate(t)} style={{ paddingLeft: 32 }}>
                <ListItemText primary={startCase(t.kind)} />
            </ListItem>
        ));

        const groups = groupBy(others, 'group');
        const templateGroups = orderBy(Object.keys(groups)).map((name, i) => {
            if (name === 'phonescreen' && !hasRole(userPermissions, 'email_templates_editor')) {
                return null;
            }
            const list = groups[name];
            const groupedByKind = groupBy(list, 'kind');
            const groupKey = `group-${name}`;
            const isGroupOpen = openSections[groupKey];

            const templatesList = Object.keys(groupedByKind).map((kind) => {
                const templates = orderBy(groupedByKind[kind], 'id');
                if (templates.length === 1) {
                    return (
                        <ListItem
                            button={true}
                            key={templates[0].id}
                            onClick={this.handleSelectTemplate(templates[0])}
                            style={{ paddingLeft: 64 }}
                        >
                            <ListItemText primary={startCase(templates[0].kind)} />
                        </ListItem>
                    );
                } else {
                    const kindKey = `kind-${name}-${kind}`;
                    const isKindOpen = openSections[kindKey];
                    const kindList = templates.map((t, j) => (
                        <ListItem
                            button={true}
                            key={t.id}
                            onClick={this.handleSelectTemplate(t)}
                            style={{ paddingLeft: 96 }}
                        >
                            <ListItemText primary={`${startCase(t.kind)} ${j}`} />
                        </ListItem>
                    ));
                    return (
                        <React.Fragment key={templates[0].id}>
                            <ListItem
                                button={true}
                                onClick={this.handleToggleSection(kindKey)}
                                style={{ paddingLeft: 64 }}
                            >
                                <ListItemText primary={startCase(templates[0].kind)} />
                                {isKindOpen ? <ExpandLess /> : <ExpandMore />}
                            </ListItem>
                            <Collapse in={isKindOpen} timeout="auto" unmountOnExit={true}>
                                <List component="div" disablePadding={true}>
                                    {kindList}
                                </List>
                            </Collapse>
                        </React.Fragment>
                    );
                }
            });

            return (
                <React.Fragment key={i}>
                    <ListItem button={true} onClick={this.handleToggleSection(groupKey)} style={{ paddingLeft: 32 }}>
                        <ListItemText primary={startCase(name)} />
                        {isGroupOpen ? <ExpandLess /> : <ExpandMore />}
                    </ListItem>
                    <Collapse in={isGroupOpen} timeout="auto" unmountOnExit={true}>
                        <List component="div" disablePadding={true}>
                            {templatesList}
                        </List>
                    </Collapse>
                </React.Fragment>
            );
        });

        let dialog;
        if (selected) {
            const deleteCallback =
                selected.userId === user.id || hasRole(userPermissions, 'email_templates_editor')
                    ? this.handleDelete(selected)
                    : null;
            const inProgress =
                pendingRequests.has('save-email-template') || pendingRequests.has('delete-email-template');
            dialog = (
                <Dialog open={true} maxWidth="md" css={styles} disableEnforceFocus={true}>
                    <EmailTemplate
                        template={selected}
                        key={selected.id}
                        readonly={!hasRole(userPermissions, 'email_templates_editor') && selected.userId !== user.id}
                        onSave={this.handleSaveTemplate}
                        saving={inProgress}
                        onCancel={this.handleCloseTemplate}
                        onDelete={deleteCallback}
                        showModalAlert={this.props.showModalAlert}
                    />
                </Dialog>
            );
        }

        const isPersonalOpen = openSections.personal;
        const isSharedOpen = openSections.shared;

        return (
            <div className="email-templates-scroll-container">
                <div className="email-templates-list">
                    <List>
                        <ListItem button={true} onClick={this.handleToggleSection('personal')}>
                            <ListItemText primary="Personal" />
                            {isPersonalOpen ? <ExpandLess /> : <ExpandMore />}
                        </ListItem>
                        <Collapse in={isPersonalOpen} timeout="auto" unmountOnExit={true}>
                            <List component="div" disablePadding={true}>
                                {personalTemplates}
                            </List>
                        </Collapse>
                        <ListItem button={true} onClick={this.handleToggleSection('shared')}>
                            <ListItemText primary="Shared" />
                            {isSharedOpen ? <ExpandLess /> : <ExpandMore />}
                        </ListItem>
                        <Collapse in={isSharedOpen} timeout="auto" unmountOnExit={true}>
                            <List component="div" disablePadding={true}>
                                {sharedTemplates}
                            </List>
                        </Collapse>
                        {templateGroups}
                    </List>
                    <div className="add-template">
                        <Button variant="text" onClick={this.handleAddTemplate}>
                            Add Template
                        </Button>
                    </div>
                </div>
                {dialog}
            </div>
        );
    }
}

const mapStateToProps = (state: State): ConnectedProps => ({
    emailTemplates: flatMap(state.emailTemplates.valueSeq().toArray()),
    pendingRequests: state.pendingRequests,
    user: state.session.user,
    userPermissions: state.session.userPermissions
});

const mapDispatchToProps: { [action in keyof ConnectedDispatch]: ConnectedDispatch[action] } = {
    deleteTemplate,
    getConfirmation,
    getEmailTemplates,
    saveTemplate,
    showModalAlert
};

export const EmailTemplates = connect<ConnectedProps, ConnectedDispatch, {}>(
    mapStateToProps,
    mapDispatchToProps
)(EmailTemplatesComponent);
